<script setup>
import { ref, computed } from "vue";

import { useCurrentMenuStore } from "../../stores/current-menu";
import { useMenuInfoStore } from "../../stores/menu-info";

import Button from "../../components/Button";

const currentMenuStore = useCurrentMenuStore();
const menuInfoStore = useMenuInfoStore();

await menuInfoStore.getMenuInfo();

const addMenu = ref(false);

const restaurants_collapsed = ref(false);

const filteredSites = computed(() => {
  return menuInfoStore.sites.filter((site) => {
    return site.menu_slug === null;
  });
});
</script>

<template>
  <div class="grid-item">
    <div class="title-container">
      <p class="title">Restaurant(s)</p>
      <div class="sites-collapse-container" @click="restaurants_collapsed = !restaurants_collapsed">
        <div class="toggle-collapse" :class="restaurants_collapsed ? 'collapsed' : ''" data-v-dfa82310="">
          <svg viewBox="0 0 18 10" fill="none" xmlns="http://www.w3.org/2000/svg" data-v-dfa82310="">
            <g clip-path="url(#a)">
              <path d="m1.061 1.061 7.63 7.629 7.629-7.629" stroke="#222127" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round"></path>
            </g>
            <defs>
              <clipPath id="a">
                <path fill="#fff" d="M0 0h17.38v9.44H0z"></path>
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>
    </div>
    <div class="content-container" :class="restaurants_collapsed ? 'inactive' : ''">
      <ul class="grid-item-sites">
        <li class="site-item" v-for="site in currentMenuStore.sortedSites">
          <div class="site">
            <img v-if="site.url" :src="`https://www.google.com/s2/favicons?domain=${site.url}&sz=32`" alt="" />
            {{ site.name }}
          </div>
          <div class="site-item__options">
            <svg-vue icon="cross" @click="currentMenuStore.removeSite(site)"></svg-vue>
          </div>
        </li>
      </ul>
      <div class="add" v-if="!addMenu" @click="addMenu = !addMenu">
        <div class="add__icon">
          <svg-vue icon="plus"></svg-vue>
        </div>
        <p>Add Restaurant</p>
      </div>
    </div>
    <Transition name="fade" mode="out-in">
      <div v-if="addMenu">
        <h3>Add Restaurants</h3>
        <ul class="add-list" v-if="filteredSites.length">
          <li v-for="site in filteredSites" @click="currentMenuStore.addSite(site)">
            {{ site.name }}
          </li>
        </ul>
        <div class="add-list" v-else>
          <div class="add-list__msg">No available restaurants found</div>
        </div>
      </div>
    </Transition>

    <div class="button-holder">
      <Button :method="currentMenuStore.saveMenu">Save Menu</Button>
      <Button :method="currentMenuStore.syncMenus"
        :buttonStyle="currentMenuStore.hasChanges ? 'button--disabled' : 'button--bordered'">{{
    currentMenuStore.hasChanges ? "Save menu before sync" : "Sync Changes"
        }}</Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../scss/partials/variables';

.grid-item {
  position: sticky;
  top: var(--inside-padding-horiz);
  height: calc(100vh - (var(--inside-padding) * 2) - var(--inside-padding-horiz));
  flex-direction: column;
  display: flex;

  @media screen and (max-width: $breakpoint-md) {
    margin-bottom: 2px;
    padding: 20px;
  }

  .title {
    font-family: var(--secondary-font);
    font-size: 18px;
    margin-bottom: 30px;

    @media screen and (max-width: $breakpoint-md) {
      margin-bottom: 2px;
    }
  }

  .title-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding-bottom: 10px;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .sites-collapse-container {
    display: none;

    @media screen and (max-width: $breakpoint-md) {
      display: flex;
    }
  }

  .inactive {
    @media screen and (max-width: $breakpoint-md) {
      display: none;
    }

    display: block;
  }

  &-sites {
    margin-bottom: 30px;

    .site-item {
      list-style: none;
      padding: 20px 0;
      position: relative;
      transition: 0.3s ease;

      .site {
        display: flex;
        align-items: center;

        img {
          margin-right: var(--grid-gap);
          width: 32px;
          height: 32px;
        }
      }

      &:hover {
        background: var(--grey-lighter);
      }

      &__options {
        position: absolute;
        top: 50%;
        right: 0px;
        transform: translateY(-40%);
        opacity: 0;
        background: var(--grey-light);
        border-radius: 100px;
        padding: 5px;
        display: flex;
        gap: 10px;
        align-items: center;
        transition: 0.3s ease;

        >* {
          cursor: pointer;
        }

        svg {
          width: 10px;
          height: 10px;
        }
      }

      &:hover {
        padding-left: 20px;

        .site-item {
          &__options {
            transform: translateY(-50%);
            right: 20px;
            opacity: 1;
          }
        }
      }
    }
  }

  .add {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
    cursor: pointer;

    &:hover {
      .add__icon {
        transform: scale(1.1);
      }
    }

    &__icon {
      width: 32px;
      height: 32px;
      background: var(--grey-light);
      border-radius: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
      transition: 0.3s ease;

      svg {
        width: 12px;
        height: 12px;
      }
    }
  }

  .add-list {
    list-style: none;
    background: var(--grey-lighter);
    margin-bottom: 30px;

    &__msg {
      padding: 10px;
    }

    li {
      cursor: pointer;
      padding: 20px;
      transition: 0.3s ease;

      &:hover {
        background: var(--grey-light);
      }
    }
  }

  .button-holder {
    margin-top: auto;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--grid-gap);
  }
}
</style>
